import React, { useEffect, useState } from 'react';
import { FiCheckSquare, FiChevronDown, FiCircle, FiMenu } from 'react-icons/fi';
import { StanButton, StanInput, StanToggle } from '@/components';
import { cn } from '@/lib';
import { Question } from '@/types/store';
import { useValidator } from '@/features/product-editor/validations/ValidationProvider';
import { useFormStoreSync } from '@/hooks/useFormStoreSync';
import { OptionInput } from './OptionInput';

// Import icons
import IcPhone from '@/assets/icons/ic-phone.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcText from '@/assets/icons/ic-text.svg';
import IcTrash from '@/assets/icons/ic-trash.svg';

interface FormFieldCardProps {
    field: Question;
    index: number;
    updateField: (index: number, field: Question) => void;
    removeField: (index: number) => void;
}

export const FormFieldCard = ({ field: initialField, index, updateField, removeField }: FormFieldCardProps) => {
    // Internal state management
    const [field, setField] = useState<Question>(initialField);

    const { questionSectionValidation } = useValidator();
    const {
        register,
        formState: { errors },
        setValue,
    } = questionSectionValidation;

    useEffect(() => {
        updateField(index, field);
    }, [field]);

    // Internal handlers
    const handleUpdateFieldContent = (content: string) => {
        setField(prev => ({ ...prev, content: content }));
    };

    // TODO: AVoid rerenders
    useEffect(() => {
        setValue(`${index}`, initialField);
    }, [errors, initialField, index, setValue]);

    const handleUpdateOptionValue = (optionIndex: number, optionValue: string) => {
        setField(prev => ({
            ...prev,
            options: prev.options?.map((opt, idx) => (idx === optionIndex ? { ...opt, value: optionValue } : opt)),
        }));
    };

    const handleRemoveOption = (optionIndex: number) => {
        setField(prev => ({
            ...prev,
            options: prev.options?.filter((_, idx) => idx !== optionIndex),
        }));
    };

    const handleAddOption = () => {
        const newOption = { value: '', id: Date.now().toString() };
        setField(prev => ({
            ...prev,
            options: [...(prev.options || []), newOption],
        }));
    };

    const handleUpdateFieldRequired = (required: boolean) => {
        setField(prev => ({ ...prev, required }));
    };

    const getIconForField = (type: string) => {
        switch (type) {
            case 'text':
                return IcText;
            case 'phone':
                return IcPhone;
            case 'select':
                return FiChevronDown;
            case 'checkbox':
                return FiCheckSquare;
            case 'radio':
                return FiCircle;
            default:
                return IcText;
        }
    };

    const getPlaceholderForField = (type: Question['type']) => {
        switch (type) {
            case 'text':
                return 'Short Answer Title';
            case 'dropdown':
                return 'Dropdown title...';
            case 'checkbox':
                return 'Checkbox title...';
            case 'radio':
                return 'Multiple choice title...';
            default:
                return '';
        }
    };

    return (
        <div
            className={cn(
                'mb-4 flex w-full flex-col items-center justify-center rounded-lg bg-[var(--bg-color-neutral-bg-weak)] px-4 shadow-[0px_6px_50px_0px_rgba(0,29,109,0.07),0px_-5px_10px_0px_rgba(0,0,0,0)_inset]',
                errors[index] ? 'border border-[var(--border-color-negative)]' : ''
            )}
        >
            <div className='flex w-full'>
                <div className='flex h-8 cursor-grab items-center active:cursor-grabbing'>
                    <FiMenu className='text-xl' />
                </div>
                <div className='mb-3 flex w-full flex-col rounded-[7px] bg-[var(--bg-color-neutral-bg-weak)] p-0 shadow-none'>
                    <div className='flex w-full items-center'>
                        <div className='w-full flex-grow'>
                            <div className='flex items-center px-3 py-2'>
                                <StanInput
                                    startIcon={getIconForField(field.type)}
                                    value={field.content}
                                    {...useFormStoreSync(register(`${index}.content`), handleUpdateFieldContent)}
                                    error={!!errors[index]?.content}
                                    hintText={errors[index]?.content?.message as string}
                                    className='mr-3 px-0'
                                    disabled={field.type === 'phone'}
                                    placeholder={getPlaceholderForField(field.type)}
                                />
                            </div>
                        </div>
                    </div>

                    {(field.type === 'checkbox' || field.type === 'radio' || field.type === 'dropdown') && (
                        <div className='flex w-full'>
                            <div className='w-full pl-[10px]'>
                                {field.options?.map((option, o) => (
                                    <OptionInput
                                        key={option.id || `option-${o}`}
                                        option={option}
                                        optionIndex={o}
                                        fieldIndex={index}
                                        handleUpdateOptionValue={handleUpdateOptionValue}
                                        handleRemoveOption={handleRemoveOption}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='flex justify-between pt-2'>
                        {(field.type === 'checkbox' || field.type === 'radio' || field.type === 'dropdown') && (
                            <StanButton
                                name='add-option-form-fields'
                                variant='link'
                                className='mt-2'
                                onClick={handleAddOption}
                                icon={IcPlus}
                            >
                                Add Option
                            </StanButton>
                        )}
                        <div className='mr-3 ml-auto flex items-center justify-end pr-0'>
                            <div className='mr-3 flex items-center justify-end'>
                                <span className='pr-1 text-xs leading-none tracking-wide'>Required</span>
                                <StanToggle
                                    id={`checkbox-${index}`}
                                    checked={field.required}
                                    onChange={e => handleUpdateFieldRequired(e.target.checked)}
                                    toggleSize='sm'
                                />
                            </div>
                            <div className='flex items-center justify-end'>
                                <StanButton
                                    variant='link'
                                    name='remove-field-form-fields'
                                    onClick={() => removeField(index)}
                                    icon={IcTrash}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
